import { FC, forwardRef, useMemo } from 'react';
import { FaShapes, FaTrashAlt } from 'react-icons/fa';
import { GiAmericanFootballHelmet } from 'react-icons/gi';
import { LuImage, LuType } from 'react-icons/lu';
import { PiBoundingBox } from 'react-icons/pi';
import { IconType } from 'react-icons';
import { shallow } from 'zustand/shallow';
import {
  AdElement,
  isCasinoAssetElement,
  isImageElement,
  isTeamAssetElement,
  isTextElement,
  isVectorElement,
} from '@adflow/types';
import theme from '@adflow/theme';
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';
import { useStore } from '../../hooks/useStore';
import { useTemplateAnimationsFeature } from '../../hooks/useTemplateAnimationsFeature';
import { CasinoIcon } from '../../icons/casino';
import ImageElementControls from './ImageElementControls';
import TextElementControls from './TextElementControls';
import TeamAssetElementControls from './TeamAssetControls';
import VectorElementControls from './VectorElementControls';
import CasinoAssetControls from './CasinoAssetControls';
import AnimateControls from './AnimateControls';

const sidebarTabStyles = {
  color: theme.palette.mcNavy.main,
  fontWeight: 600,
  fontSize: 'sm',
  px: 4,
  py: 1.5,
  borderRadius: 'md',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'gray.200',
  bg: 'white',
  _selected: {
    color: theme.palette.mcBlue.main,
    borderColor: theme.palette.mcBlue.main,
    bg: 'white',
  },
  _hover: {
    borderColor: theme.palette.mcBlue.light,
  },
  _focus: { boxShadow: 'none' },
  _focusVisible: { boxShadow: 'outline' },
};

const rightSidebarShellProps = {
  display: 'flex',
  py: 4,
  borderLeftWidth: 1,
  direction: 'column',
  minW: '350px',
  maxW: '450px',
  width: '25vw',
  minH: 0,
  flexShrink: 0,
  overflow: 'hidden',
  height: '100%',
  bg: 'white',
} as const;

function getSelectedElementIcon(element: AdElement): IconType {
  switch (element.type) {
    case 'SVG':
      return FaShapes;
    case 'IMAGE':
      return LuImage;
    case 'TEAM_ASSET':
      return GiAmericanFootballHelmet;
    case 'CASINO_ASSET':
      return CasinoIcon;
    case 'VECTOR':
      return PiBoundingBox;
    default:
      return LuType;
  }
}

const Rightsidebar: FC = () => {
  const animationsEnabled = useTemplateAnimationsFeature();
  const s = useStore(
    (state) => ({
      elements: state.elements,
      selectedElementId: state.selectedElementId,
      importSourceType: state.importSourceType,
    }),
    shallow,
  );

  const selectedElement = useMemo(() => {
    return s.elements.find((el) => el.id === s.selectedElementId);
  }, [s.elements, s.selectedElementId]);

  if (selectedElement == null) {
    return null;
  }

  const propertyControls = (
    <>
      {isTextElement(selectedElement) && (
        <TextElementControls
          // The "key" prop is provided as it enables React to remount the component when it changes
          key={selectedElement.id}
          selectedElement={selectedElement}
          importSourceType={s.importSourceType}
        />
      )}
      {isImageElement(selectedElement) && (
        <ImageElementControls key={selectedElement.id} selectedElement={selectedElement} />
      )}
      {isTeamAssetElement(selectedElement) && (
        <TeamAssetElementControls key={selectedElement.id} selectedElement={selectedElement} />
      )}
      {isCasinoAssetElement(selectedElement) && (
        <CasinoAssetControls key={selectedElement.id} selectedElement={selectedElement} />
      )}
      {isVectorElement(selectedElement) && (
        <VectorElementControls key={selectedElement.id} selectedElement={selectedElement} />
      )}
    </>
  );

  return (
    <Flex {...rightSidebarShellProps} data-test-id="rightsidebar">
      <Flex
        alignItems="flex-start"
        justifyContent="space-between"
        px={6}
        direction="column"
        gap={2}
        height="100%"
        minH={0}
        overflowY="auto"
      >
        <Flex width="100%" direction="column">
          <Flex alignItems="center" mb="4" color={theme.palette.mcNavy.main}>
            <Icon as={getSelectedElementIcon(selectedElement)} fontSize="xl" mr="2" />
            <Heading
              as="h2"
              size="md"
              fontWeight={800}
              noOfLines={1}
              data-test-id="selected-element-name"
            >
              {selectedElement.name}
            </Heading>
          </Flex>
          <Tabs variant="unstyled">
            <TabList mb={4} gap={2} data-test-id="rightsidebar-tabs">
              <Tab {...sidebarTabStyles} data-test-id="properties-tab">
                Properties
              </Tab>
              {animationsEnabled && (
                <Tab {...sidebarTabStyles} data-test-id="animate-tab">
                  Animate
                </Tab>
              )}
            </TabList>
            <TabPanels>
              <TabPanel p={0} data-test-id="properties-panel">
                {propertyControls}
              </TabPanel>
              {animationsEnabled && (
                <TabPanel p={0} data-test-id="animate-panel">
                  <AnimateControls key={selectedElement.id} targetId={selectedElement.id} />
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </Flex>
        <Box alignSelf={'flex-end'}></Box>
      </Flex>
    </Flex>
  );
};

const DeleteBtn = forwardRef<HTMLButtonElement, { onClick: () => void }>((props, ref) => {
  return (
    <Button
      ref={ref}
      px={2}
      colorScheme="red"
      onClick={props.onClick}
      data-test-id="delete-element"
      size="sm"
    >
      <Icon mr={1} as={FaTrashAlt} />
      Delete
    </Button>
  );
});

DeleteBtn.displayName = 'DeleteBtn';

export default Rightsidebar;
